import dayjs, { Dayjs } from 'dayjs'

/** Units a surface can offer as a peer. */
export type DateNavigatorUnit = 'week' | 'month' | 'quarter' | 'year'

export interface DateNavigatorWindow {
  start: Dayjs
  end: Dayjs
  span: DateNavigatorUnit
}

/**
 * Where the week starts, as a day index (0 Sunday … 6 Saturday).
 *
 * Not a detail: a week is the one unit whose boundaries the *surface* owns, and the surface's answer
 * is a user setting. The calendar's Mon-Fri / Sun-Thu preference is independent of the dayjs locale,
 * and a window built on the locale's answer while the grid uses the setting's lands a week out.
 * Omitted means "whatever the active dayjs locale says", which is the right default for a surface
 * with no setting of its own.
 */
export type WeekStart = 0 | 1 | 2 | 3 | 4 | 5 | 6

/**
 * Whether a month holds nothing to show, by its first day. The navigator offers such a month
 * disabled rather than hiding it, so the reason it cannot be reached stays on screen.
 */
export type DisabledMonth = (month: Dayjs) => boolean

export function startOfWeek(anchor: Dayjs, weekStart?: WeekStart): Dayjs {
  if (weekStart === undefined) return anchor.startOf('week')
  return anchor.startOf('day').subtract((anchor.day() - weekStart + 7) % 7, 'day')
}

/** First day of the quarter containing `anchor`. dayjs puts `startOf('quarter')` behind the
 * `quarterOfYear` plugin, which this app does not load, and the arithmetic is one line. */
export function startOfQuarter(anchor: Dayjs): Dayjs {
  return anchor.month(Math.floor(anchor.month() / 3) * 3).startOf('month')
}

/** The window of `unit` that contains `anchor`. */
export function windowFor(unit: DateNavigatorUnit, anchor: Dayjs, weekStart?: WeekStart): DateNavigatorWindow {
  if (unit === 'week') {
    const start = startOfWeek(anchor, weekStart)
    return { start, end: start.add(6, 'day').endOf('day'), span: 'week' }
  }
  if (unit === 'quarter') {
    const start = startOfQuarter(anchor)
    return { start, end: start.add(2, 'month').endOf('month'), span: 'quarter' }
  }
  return { start: anchor.startOf(unit), end: anchor.endOf(unit), span: unit }
}

/** One step back or forward, by the unit the window is in. */
export function stepWindow(window: DateNavigatorWindow, direction: -1 | 1, weekStart?: WeekStart): DateNavigatorWindow {
  if (window.span === 'quarter') return windowFor('quarter', window.start.add(direction * 3, 'month'))
  return windowFor(window.span, window.start.add(direction, window.span), weekStart)
}

/** Where "back to now" lands: the window of the same unit containing today. */
export function todayWindow(window: DateNavigatorWindow, now: Dayjs = dayjs(), weekStart?: WeekStart): DateNavigatorWindow {
  return windowFor(window.span, now, weekStart)
}

export function containsToday(window: DateNavigatorWindow, now: Dayjs = dayjs()): boolean {
  return !now.isBefore(window.start, 'day') && !now.isAfter(window.end, 'day')
}

/** Every month the window touches, as first days. A week can straddle two, a quarter spans three. */
export function monthsIn(window: DateNavigatorWindow): Dayjs[] {
  const months: Dayjs[] = []
  let month = window.start.startOf('month')
  while (!month.isAfter(window.end, 'month')) {
    months.push(month)
    month = month.add(1, 'month')
  }
  return months
}

/**
 * Whether a window has nothing to show. Only true when *every* month it covers is empty: a quarter
 * with one month of data is still worth opening, and a week straddling a month end stays reachable
 * from whichever side has the data.
 */
export function windowDisabled(window: DateNavigatorWindow, disabledMonth?: DisabledMonth): boolean {
  if (!disabledMonth) return false
  return monthsIn(window).every((month) => disabledMonth(month))
}
